import React, { ReactNode } from 'react';
import { Dialog } from '@base-ui-components/react/dialog';
import { CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';
import Button from './Button';
import { cn } from '@/utils/cn';

type AlertType = 'success' | 'error' | 'warning' | 'info';

interface AlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type?: AlertType;
  title: string;
  description?: string;
  confirmText?: string;
  onConfirm?: () => void;
  showCancel?: boolean;
  cancelText?: string;
  onCancel?: () => void;
}

const iconMap: Record<AlertType, ReactNode> = {
  success: <CheckCircle size={48} className="text-green-500" />,
  error: <XCircle size={48} className="text-red-500" />,
  warning: <AlertCircle size={48} className="text-yellow-500" />,
  info: <Info size={48} className="text-blue-500" />,
};

export default function AlertDialog({
  open,
  onOpenChange,
  type = 'info',
  title,
  description,
  confirmText = '确定',
  onConfirm,
  showCancel = false,
  cancelText = '取消',
  onCancel,
}: AlertDialogProps) {
  const handleConfirm = () => {
    onConfirm?.();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* 遮罩层 */}
        <Dialog.Backdrop
          className={cn(
            'fixed inset-0 min-h-dvh bg-black/50 z-50',
            'transition-opacity duration-300',
            'data-[starting-style]:opacity-0',
            'data-[ending-style]:opacity-0'
          )}
        />

        {/* 居中弹窗 */}
        <Dialog.Popup
          className={cn(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'w-[85%] max-w-[320px]',
            'bg-bg-card rounded-2xl',
            'shadow-xl',
            'transition-all duration-300 ease-out',
            'data-[starting-style]:opacity-0 data-[starting-style]:scale-95',
            'data-[ending-style]:opacity-0 data-[ending-style]:scale-95',
            'outline-none z-50'
          )}
        >
          {/* 内容区域 */}
          <div className="flex flex-col items-center px-6 py-8">
            {/* 图标 */}
            <div className="mb-4">{iconMap[type]}</div>

            {/* 标题 */}
            <Dialog.Title className="text-lg font-medium text-text-primary text-center mb-2">
              {title}
            </Dialog.Title>

            {/* 描述 */}
            {description && (
              <Dialog.Description className="text-sm text-text-secondary text-center mb-6">
                {description}
              </Dialog.Description>
            )}

            {/* 按钮组 */}
            <div className={cn('w-full flex gap-3', !description && 'mt-4')}>
              {showCancel && (
                <Button variant="default" block onClick={handleCancel}>
                  {cancelText}
                </Button>
              )}
              <Button variant="primary" block onClick={handleConfirm}>
                {confirmText}
              </Button>
            </div>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
